import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Popper,
  PopperProps,
  TextField,
  styled,
} from '@mui/material'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import { colorPalette } from '../../../../style/colorPalette'

type SlackSelectProps<Option> = {
  options: Option[]
  value: Option | null
  onChange: (event: unknown, value: Option | null) => void
  getOptionLabel: (option: Option) => string
  isOptionEqualToValue: (option: Option, value: Option) => boolean
  disabled: boolean
}

// 共有ラッパーの components/inputs/Autocomplete は ReferencedEntity と
// 1打鍵ごとの非同期検索を前提としており、事前取得した Slack の一覧
// （SlackWorkspace / SlackUser）には合わない。components/select は
// インクリメンタル検索を持たず、メンバー数の多いワークスペースで
// ユーザーを絞り込めないため、MUI Autocomplete を直接使う。
export const SlackSelect = <Option,>({
  options,
  value,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  disabled,
}: SlackSelectProps<Option>) => (
  <Autocomplete
    size="small"
    fullWidth={true}
    disabled={disabled}
    options={options}
    value={value}
    // MUIは選び直しの判定を参照の一致で行うため、value に選択肢とは別の
    // オブジェクトを渡していると、同じ項目を選び直しても onChange が呼ばれる。
    onChange={(event, newValue) => {
      if (newValue && value && isOptionEqualToValue(newValue, value)) return
      onChange(event, newValue)
    }}
    getOptionLabel={getOptionLabel}
    isOptionEqualToValue={isOptionEqualToValue}
    renderInput={renderStandardInput}
    PopperComponent={BottomStartPopper}
    // 非活性時は矢印アイコンを消し、編集不可であることを見た目でも示す
    // （components/inputs/Autocomplete と同じ扱い）。
    forcePopupIcon={!disabled}
    // MUI既定の ArrowDropDown（塗りつぶしの三角）は、他のセルが使う
    // components/select・components/inputs/Autocomplete の山型と形が違い、
    // 同じ単票の中で記号が混在する。それらと同じアイコンに揃える。
    popupIcon={<PopupIcon />}
    // MUIの既定文言は英語のため、他のAutocompleteと同様に空文字で抑止する。
    noOptionsText=""
  />
)

// セル内に収めるため下線なしの standard variant で描画する。
const renderStandardInput = (params: AutocompleteRenderInputParams) => (
  <TextField
    {...params}
    variant="standard"
    InputProps={{ ...params.InputProps, disableUnderline: true }}
  />
)
const PopupIcon = styled(ExpandMoreRounded)({
  color: colorPalette.monotone[4],
})
// インライン関数で渡すとレンダリング毎に別コンポーネントとして扱われ、
// Popper が再マウントされるためモジュールスコープに固定する。
const BottomStartPopper = (props: PopperProps) => (
  <Popper {...props} placement="bottom-start" />
)
